import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useDebounce from "hooks/route-rules/useDebounce";
import useLocationRestrictions from "hooks/location-restrictions/useLocationRestrictions";
import LocationRestrictionDrawer from "./LocationRestrictionDrawer";
import CardDropdown from "components/common/CardDropdown";
import { Dropdown } from "react-bootstrap";
import { OLIVEEB_BACKEND_SERVICE_OPTIONS } from "constants";

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  // Invalid date fallback
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
};

const formatUpper = (value) => (value ? String(value).toUpperCase() : "—");

const { Text } = Typography;

export default function LocationRestrictionsPage() {
  const [page, setPage] = useState(0); // 0-based (Spring style)
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);
  const [checkSubmitting, setCheckSubmitting] = useState(false);
  const [checkResponse, setCheckResponse] = useState(null);
  const [checkForm] = Form.useForm();
  // Optional table filter: null => show all restrictions.
  const [filterOliveebApp, setFilterOliveebApp] = useState(null);

  // Last-used app for drawers (create/check); persisted for convenience.
  const [drawerOliveebApp, setDrawerOliveebApp] = useState(() => {
    const stored = sessionStorage.getItem("locationRestrictionsApp");
    return stored ? String(stored) : null;
  });

  const {
    restrictions,
    loading,
    totalElements,
    fetchAll,
    checkAccess,
    create,
    update,
    updateActive,
    updateAccessType,
    remove,
  } = useLocationRestrictions(page, pageSize, filterOliveebApp);

  const oliveebAppOptions = useMemo(() => OLIVEEB_BACKEND_SERVICE_OPTIONS, []);

  const isSearchMode = debouncedSearch.trim().length >= 3;
  const isSearchRequest = loading && isSearchMode;
  const SEARCH_PAGE_SIZE = 1000;

  useEffect(() => {
    sessionStorage.setItem(
      "locationRestrictionsApp",
      drawerOliveebApp ? String(drawerOliveebApp) : "",
    );
    setCheckResponse(null);
    checkForm.setFieldsValue({
      oliveebApp: drawerOliveebApp || null,
    });
  }, [drawerOliveebApp, checkForm]);

  useEffect(() => {
    setPage(0);
  }, [filterOliveebApp]);

  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (!trimmed || trimmed.length < 3) {
      fetchAll(page, pageSize);
      return;
    }

    // Backend controller doesn't expose a search endpoint; in search mode we fetch a larger page
    // and filter client-side.
    fetchAll(0, SEARCH_PAGE_SIZE);
  }, [debouncedSearch, page, pageSize, fetchAll]);

  const filteredData = useMemo(() => {
    const data = Array.isArray(restrictions) ? restrictions : [];
    const term = debouncedSearch.trim().toLowerCase();
    if (!term || term.length < 3) return data;

    return data.filter((r) => {
      const haystack = [
        r?.id,
        r?.oliveebApp,
        r?.app,
        r?.country,
        r?.state,
        r?.county,
        r?.city,
        r?.accessType,
        r?.updatedBy,
        r?.createdAt,
        r?.updatedAt,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [restrictions, debouncedSearch]);

  const formatLocationLabel = useCallback(
    (record) =>
      `${formatUpper(record?.country)}${
        record?.state ? `-${String(record.state).toUpperCase()}` : ""
      }${record?.county ? ` / ${record.county}` : ""}${
        record?.city ? ` / ${record.city}` : ""
      }`,
    [],
  );

  const confirmDeactivate = useCallback(
    (record) =>
      new Promise((resolve) => {
        Modal.confirm({
          title: "Deactivate restriction?",
          icon: <ExclamationCircleOutlined />,
          content: (
            <div>
              This will deactivate the restriction for{" "}
              <strong>{formatLocationLabel(record)}</strong>.
            </div>
          ),
          okText: "Yes, deactivate",
          okType: "danger",
          cancelText: "No",
          onOk: async () => {
            try {
              await updateActive(record.id, false);
              resolve(true);
            } catch {
              resolve(false);
            }
          },
          onCancel: () => resolve(false),
        });
      }),
    [formatLocationLabel, updateActive],
  );

  const confirmBlock = useCallback(
    (record) =>
      new Promise((resolve) => {
        Modal.confirm({
          title: "Block access for this restriction?",
          icon: <ExclamationCircleOutlined />,
          content: (
            <div>
              This will set access to <strong>UNALLOWED</strong> for{" "}
              <strong>{formatLocationLabel(record)}</strong>.
            </div>
          ),
          okText: "Yes, block",
          okType: "danger",
          cancelText: "No",
          onOk: async () => {
            try {
              await updateAccessType(record.id, "UNALLOWED");
              resolve(true);
            } catch {
              resolve(false);
            }
          },
          onCancel: () => resolve(false),
        });
      }),
    [formatLocationLabel, updateAccessType],
  );

  const confirmDelete = useCallback(
    (record) =>
      new Promise((resolve) => {
        Modal.confirm({
          title: "Delete restriction?",
          icon: <ExclamationCircleOutlined />,
          content: (
            <div>
              Delete this restriction for <strong>{formatLocationLabel(record)}</strong>
              ? This action cannot be undone.
            </div>
          ),
          okText: "Yes, delete",
          okType: "danger",
          cancelText: "No",
          onOk: async () => {
            try {
              await remove(record.id);
              resolve(true);
            } catch {
              resolve(false);
            }
          },
          onCancel: () => resolve(false),
        });
      }),
    [formatLocationLabel, remove],
  );

  const safeApiCall = useCallback(async (fn) => {
    try {
      await fn();
      return true;
    } catch {
      // Errors are handled (toast) in the hook; swallow to avoid unhandled rejections.
      return false;
    }
  }, []);

  const detectDecision = (response) => {
    if (!response || typeof response !== "object") return null;
    const decision =
      response.allowed ??
      response.isAllowed ??
      response.permitted ??
      response.accessAllowed ??
      response.success;
    return typeof decision === "boolean" ? decision : null;
  };

  const getCheckReasonDescription = (response, decision) => {
    const reason = response?.reason ? String(response.reason).trim() : "";
    if (!reason) return undefined;

    const lower = reason.toLowerCase();
    if (decision === true && lower === "access allowed") return undefined;
    if (decision === false && lower === "access unallowed") return undefined;
    return reason;
  };

  const handleCheckAccess = useCallback(async (values) => {
    const payload = {
      oliveebApp: String(values.oliveebApp || "").trim(),
      country: String(values.country || "").trim().toLowerCase(),
      state: String(values.state || "").trim().toLowerCase() || null,
      county: String(values.county || "").trim().toLowerCase() || null,
      city: String(values.city || "").trim().toLowerCase() || null,
    };

    setCheckSubmitting(true);
    setCheckResponse(null);
    try {
      const res = await checkAccess(payload);
      setCheckResponse(res);
    } finally {
      setCheckSubmitting(false);
    }
  }, [checkAccess]);

  const columns = useMemo(
    () => [
      {
        title: "App Name",
        dataIndex: "oliveebApp",
        key: "oliveebApp",
        width: 230,
        ellipsis: true,
        render: (_, record) =>
          record?.oliveebApp || record?.app ? String(record.oliveebApp || record.app) : "—",
      },
      {
        title: "Country",
        dataIndex: "country",
        key: "country",
        width: 120,
        ellipsis: true,
        render: (value) => formatUpper(value),
      },
      {
        title: "State",
        dataIndex: "state",
        key: "state",
        width: 110,
        ellipsis: true,
        render: (value) => (value ? String(value).toUpperCase() : "—"),
      },
      {
        title: "County",
        dataIndex: "county",
        key: "county",
        width: 160,
        ellipsis: true,
        render: (value) => value || "—",
      },
      {
        title: "City",
        dataIndex: "city",
        key: "city",
        width: 160,
        ellipsis: true,
        render: (value) => value || "—",
      },
      {
        title: "Access Type",
        dataIndex: "accessType",
        key: "accessType",
        width: 120,
        render: (value) =>
          value === "UNALLOWED" ? (
            <Tag color="red">UNALLOWED</Tag>
          ) : (
            <Tag color="green">ALLOWED</Tag>
          ),
      },
      {
        title: "Status",
        dataIndex: "active",
        key: "active",
        width: 110,
        render: (active) =>
          active ? <Tag color="green">Active</Tag> : <Tag color="default">Inactive</Tag>,
      },
      {
        title: "Actions",
        key: "actions",
        width: 90,
        render: (_, record) => (
          <CardDropdown drop="start">
            <div className="py-2 fw-normal">
              <Dropdown.Item
                onClick={() => {
                  setEditing(record);
                  setDrawerOpen(true);
                }}
              >
                Edit
              </Dropdown.Item>

              <Dropdown.Item
                onClick={async () => {
                  if (record.active) {
                    await confirmDeactivate(record);
                    return;
                  }
                  await safeApiCall(() => updateActive(record.id, true));
                }}
              >
                {record.active ? "Deactivate" : "Activate"}
              </Dropdown.Item>

              <Dropdown.Item
                onClick={async () => {
                  if (record.accessType !== "UNALLOWED") {
                    await confirmBlock(record);
                    return;
                  }
                  await safeApiCall(() =>
                    updateAccessType(record.id, "ALLOWED"),
                  );
                }}
              >
                {record.accessType === "UNALLOWED" ? "Allow" : "Block"}
              </Dropdown.Item>

              <Dropdown.Divider as="div" />
              <Dropdown.Item
                className="text-danger"
                onClick={() => {
                  confirmDelete(record);
                }}
              >
                Delete
              </Dropdown.Item>
            </div>
          </CardDropdown>
        ),
      },
    ],
    [
      confirmDeactivate,
      confirmBlock,
      confirmDelete,
      safeApiCall,
      updateActive,
      updateAccessType,
    ],
  );

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (editing?.id) {
        await update(editing.id, {
          ...(payload || {}),
          active: typeof editing.active === "boolean" ? editing.active : true,
        });
      } else {
        await create(payload);
      }
      setDrawerOpen(false);
      setEditing(null);
      return true;
    } catch {
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const data = Array.isArray(restrictions) ? restrictions : [];

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="map-marker-alt"
          title="Location Restrictions"
          subtitle="Allow or block access by country/state/county/city at the gateway."
          className="mb-3 mt-3 flex-1"
        />
      </div>

      {/* filter bar (match payout tables layout) */}
      <Row gutter={[10, 10]} style={{ marginBottom: 16, alignItems: "center" }}>
        <Col>
          <Select
            showSearch
            allowClear
            placeholder="Filter by App Name"
            options={oliveebAppOptions}
            value={filterOliveebApp}
            onChange={(val) => setFilterOliveebApp(val || null)}
            optionFilterProp="label"
            filterOption={(input, option) =>
              String(option?.label || "")
                .toLowerCase()
                .includes(String(input || "").toLowerCase())
            }
            style={{ width: 200 }}
          />
        </Col>

        <Col>
          <Input
            placeholder="Search country/state/county/city"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            allowClear
            style={{ width: 250 }}
            suffix={isSearchRequest ? <Spin size="small" /> : null}
          />
        </Col>

        <Col style={{ marginLeft: "auto" }}>
          <Space>
            <Button
              loading={loading}
              onClick={() =>
                isSearchMode ? fetchAll(0, SEARCH_PAGE_SIZE) : fetchAll(page, pageSize)
              }
            >
              Refresh
            </Button>
            <Button
              onClick={() => {
                setCheckResponse(null);
                checkForm.setFieldsValue({
                  oliveebApp: drawerOliveebApp || null,
                });
                setCheckOpen(true);
              }}
            >
              Check Access
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                setDrawerOpen(true);
              }}
            >
              New Restriction
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        rowKey={(r) => r.id}
        columns={columns}
        dataSource={isSearchMode ? filteredData : data}
        loading={loading}
        className="custom-table"
        size="small"
        scroll={{ x: 1300 }}
        expandable={{
          expandIconColumnIndex: 0,
          expandIcon: ({ expanded, onExpand, record }) => (
            <span
              onClick={(e) => onExpand(record, e)}
              style={{
                cursor: "pointer",
                userSelect: "none",
                display: "inline-block",
                width: 16,
                textAlign: "center",
                color: "#6b7280",
                fontWeight: 700,
              }}
              aria-label={expanded ? "Collapse row" : "Expand row"}
            >
              {expanded ? "−" : "+"}
            </span>
          ),
          expandedRowRender: (record) => (
            <div style={{ padding: 8 }}>
              <p className="mb-1 pb-0">
                <strong>ID:</strong>{" "}
                <Text
                  copyable={{ text: record.id, tooltips: ["Copy ID", "Copied!"] }}
                  style={{ fontSize: 12, fontFamily: "monospace" }}
                >
                  {record.id}
                </Text>
              </p>
              <p className="mb-1 pb-0">
                <strong>App Name:</strong>{" "}
                {record?.oliveebApp || record?.app ? String(record.oliveebApp || record.app) : "—"}
              </p>
              <p className="mb-1 pb-0">
                <strong>Country:</strong> {formatUpper(record.country)}
              </p>
              <p className="mb-1 pb-0">
                <strong>State:</strong>{" "}
                {record.state ? String(record.state).toUpperCase() : "—"}
              </p>
              <p className="mb-1 pb-0">
                <strong>County:</strong> {record.county || "—"}
              </p>
              <p className="mb-1 pb-0">
                <strong>City:</strong> {record.city || "—"}
              </p>
              <p className="mb-1 pb-0">
                <strong>Access Type:</strong> {record.accessType || "—"}
              </p>
              <p className="mb-1 pb-0">
                <strong>Active:</strong> {record.active ? "true" : "false"}
              </p>
              <p className="mb-1 pb-0">
                <strong>Created At:</strong> {formatDateTime(record.createdAt)}
              </p>
              <p className="mb-1 pb-0">
                <strong>Updated At:</strong> {formatDateTime(record.updatedAt)}
              </p>
              <p className="mb-1 pb-0">
                <strong>Updated By:</strong>{" "}
                {record.updatedBy ? (
                  <Text
                    copyable={{
                      text: record.updatedBy,
                      tooltips: ["Copy Updated By", "Copied!"],
                    }}
                    style={{ fontSize: 12, fontFamily: "monospace" }}
                  >
                    {record.updatedBy}
                  </Text>
                ) : (
                  "—"
                )}
              </p>
            </div>
          ),
          rowExpandable: () => true,
        }}
        pagination={
          isSearchMode
            ? false
            : {
                current: page + 1,
                pageSize,
                total: totalElements,
                showSizeChanger: true,
                onChange: (nextPage, nextSize) => {
                  setPage(Math.max(0, (nextPage || 1) - 1));
                  setPageSize(nextSize || 10);
                },
              }
        }
        locale={{
          emptyText: loading ? (
            <div style={{ padding: 24 }}>
              <Spin />
            </div>
          ) : (
            <Empty
              description={
                searchTerm.trim()
                  ? `No restrictions found for "${searchTerm.trim()}"`
                  : "No location restrictions have been created yet"
              }
            />
          ),
        }}
      />

      <LocationRestrictionDrawer
        open={drawerOpen}
        onClose={() => {
          if (submitting) return;
          setDrawerOpen(false);
          setEditing(null);
        }}
        initialValues={editing}
        submitting={submitting}
        onSubmit={handleSubmit}
        oliveebApp={drawerOliveebApp}
        oliveebAppOptions={oliveebAppOptions}
        onOliveebAppChange={setDrawerOliveebApp}
      />

      <Drawer
        title={
          <span style={{ fontFamily: "Inter", fontSize: 15 }}>
            Check Location Access
          </span>
        }
        width={520}
        open={checkOpen}
        onClose={() => {
          if (checkSubmitting) return;
          setCheckOpen(false);
        }}
        destroyOnClose
        zIndex={1040}
      >
        <Form form={checkForm} layout="vertical" onFinish={handleCheckAccess}>
          <Form.Item
            label="App Name"
            name="oliveebApp"
            rules={[
              { required: true, message: "App Name is required" },
            ]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Select App Name"
              options={oliveebAppOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label || "")
                  .toLowerCase()
                  .includes(String(input || "").toLowerCase())
              }
              onChange={(val) => setDrawerOliveebApp(val || null)}
            />
          </Form.Item>

          <Form.Item
            label="Country"
            name="country"
            rules={[{ required: true, message: "Country is required" }]}
          >
            <Input placeholder="e.g. Uganda" />
          </Form.Item>

          <Form.Item label="State (optional)" name="state">
            <Input placeholder="e.g. CENTRAL" />
          </Form.Item>

          <Form.Item label="County (optional)" name="county">
            <Input placeholder="e.g. Wakiso" />
          </Form.Item>

          <Form.Item label="City (optional)" name="city">
            <Input placeholder="e.g. Entebbe" />
          </Form.Item>

          <div style={{ textAlign: "right", marginTop: 12 }}>
            <Button
              onClick={() => setCheckOpen(false)}
              disabled={checkSubmitting}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              loading={checkSubmitting}
              onClick={() => checkForm.submit()}
            >
              Check
            </Button>
          </div>
        </Form>

        {checkSubmitting ? (
          <div style={{ paddingTop: 8 }}>
            <Spin />
          </div>
        ) : checkResponse ? (
          <div style={{ paddingTop: 8 }}>
            {(() => {
              const decision = detectDecision(checkResponse);
              if (decision === true) {
                return (
                  <Alert
                    type="success"
                    showIcon
                    message="Access Allowed"
                    description={
                      getCheckReasonDescription(checkResponse, decision) ||
                      "The gateway check indicates this location is allowed."
                    }
                  />
                );
              }
              if (decision === false) {
                return (
                  <Alert
                    type="error"
                    showIcon
                    message="Access Unallowed"
                    description={
                      getCheckReasonDescription(checkResponse, decision) ||
                      "The gateway check indicates this location is unallowed."
                    }
                  />
                );
              }
              return (
                <Alert
                  type="info"
                  showIcon
                  message="Check response"
                  description={
                    getCheckReasonDescription(checkResponse, decision) ||
                    "Received a response from the gateway."
                  }
                />
              );
            })()}
          </div>
        ) : null}
      </Drawer>
    </>
  );
}
